import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import useSWR from 'swr';
dayjs.extend(utc);
dayjs.extend(timezone);

// Hooks
import useUser from '@/hooks/swr/useUser';

// Utils
import { get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { SCHEDULER_POSTS } from '@/routes/apiEndpoints';

const useSchedulerPosts = ({
    activeSPID,
    activeGroup = undefined,
    params = {},
    options = {},
    filter_post_status = 'all'
} = {}) => {
    const [stackedPosts, setStackedPosts] = useState(null);
    const [filteredPosts, setFilteredPosts] = useState(null);
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const userTimezone = user?.settings?.timezone ?? 'Europe/Paris';
    const dateNow = dayjs().utc().tz(userTimezone).format();

    const fetcher = async (url, params) => {
        const responses = await Promise.all(
            url.map(async (request) => {
                const res = await get(request, params);
                return res.data.posts.map((post) => {
                    const isFuture =
                        dayjs(post.scheduled_at).utc().tz(userTimezone).format() > dateNow;
                    const startDate = dayjs(post.scheduled_at)
                        .utc()
                        .tz(userTimezone)
                        .format('YYYY-MM-DD HH:00:00');
                    return {
                        ...post,
                        start: startDate,
                        startRef: startDate, // useful for stacked posts
                        startEditable: isFuture
                    };
                });
            })
        );

        return responses.flat();
    };

    const {
        data,
        mutate,
        error: schedulerPostsError
    } = useSWR(
        params && (activeSPID || activeGroup)
            ? [
                  activeGroup
                      ? activeGroup?.social_profiles?.map((sp) => {
                            return injectEndpointUrlParams(SCHEDULER_POSTS, { SPID: sp.id });
                        })
                      : [injectEndpointUrlParams(SCHEDULER_POSTS, { SPID: activeSPID })],
                  ...JSON.stringify(params)
              ]
            : null,
        ([url]) => fetcher(url, params),
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            ...options
        }
    );
    const isLoadingSchedulerPosts = !data && !stackedPosts && !schedulerPostsError;

    useEffect(() => {
        // Reload data when we switching social profile
        if (!isLoadingSchedulerPosts) {
            mutate(null);
        }
    }, [activeSPID]);

    useEffect(() => {
        if (data) {
            if (filter_post_status === 'all') {
                setFilteredPosts(data);
            } else {
                setFilteredPosts(data.filter((post) => post.state === filter_post_status));
            }
        } else {
            setFilteredPosts(data);
        }
    }, [data, filter_post_status]);

    useEffect(() => {
        if (filteredPosts) {
            const accumulatedPosts = {
                hours: {},
                days: {}
            };

            filteredPosts.map((post) => {
                const { startRef } = post;
                const day = startRef.split(' ')[0];
                ['hours', 'days'].map((time) => {
                    const key = time === 'days' ? day : startRef;

                    if (accumulatedPosts[time][key]) {
                        accumulatedPosts[time][key].push(post);
                    } else {
                        accumulatedPosts[time][key] = [post];
                    }
                });
            });

            setStackedPosts({
                hours: accumulatedPosts.hours,
                days: accumulatedPosts.days
            });
        } else {
            setStackedPosts(null);
        }
    }, [filteredPosts]);

    return {
        schedulerPosts: filteredPosts,
        schedulerStackedPosts: stackedPosts,
        mutateSchedulerPosts: mutate,
        schedulerPostsError,
        isLoadingSchedulerPosts
    };
};

export default useSchedulerPosts;
